import { MapStore } from '../UseMapStore';
import { StateCreator } from 'zustand';

import { Api } from '@services';
import { toast } from '@components';

export interface MapDataSlice {
  mapImg: any;
  setMapImg: (val: any) => void;

  newMapImg: any;
  setNewMapImg: (val: any) => void;

  mapKey: string | null;
  setMapKey: (val: string | null) => void;

  overlayFC: any;
  setOverlayFC: (val: any) => void;

  placeFC: any;
  setPlaceFC: (val: any) => void;

  gatewayFC: any;
  setGatewayFC: (val: any) => void;

  assetFC: any;
  setAssetFC: (val: any) => void;

  stationaryFC: any;
  setStationaryFC: (val: any) => void;

  scale: any;
  setScale: (val: any) => void;

  placeList: any;
  setPlaceList: (val: any) => void;

  assetList: any;
  setAssetList: (val: any) => void;

  gatewayList: any;
  setGatewayList: (val: any) => void;

  stationaryList: any;
  setStationaryList: (val: any) => void;

  isHydrating: boolean;
  setIsHydrating: (val: boolean) => void;

  fetchMapImage: () => any;
  fetchMapOveraly: () => any;

  fetchMapGateways: (payload: any) => any;
  fetchMapPlaces: (payload: any) => any;
  fetchMapAssets: (payload: any) => any;
  fetchMapStationary: (payload: any) => any;

  imageRefetch: () => void;
  setImageRefetch: (fn: () => void) => void;

  overlayRefetch: () => void;
  setOverlayRefetch: (fn: () => void) => void;

  resetAsset: () => void;
  resetPlace: () => void;
  resetGateway: () => void;
  resetStationary: () => void;

  submitUpgrade: () => Promise<boolean>;
  saveMap: () => Promise<void>;
}

// --- Initial state for easy reset ---
export const mapDataInitialState: Omit<
  MapDataSlice,
  | 'setMapImg'
  | 'setNewMapImg'
  | 'setMapKey'
  | 'setOverlayFC'
  | 'setPlaceFC'
  | 'setGatewayFC'
  | 'setAssetFC'
  | 'setStationaryFC'
  | 'setScale'
  | 'setPlaceList'
  | 'setAssetList'
  | 'setGatewayList'
  | 'setStationaryList'
  | 'setIsHydrating'
  | 'fetchMapImage'
  | 'fetchMapOveraly'
  | 'fetchMapGateways'
  | 'fetchMapPlaces'
  | 'fetchMapAssets'
  | 'fetchMapStationary'
  | 'setImageRefetch'
  | 'setOverlayRefetch'
  | 'resetAsset'
  | 'resetPlace'
  | 'resetGateway'
  | 'resetStationary'
  | 'submitUpgrade'
  | 'saveMap'
> = {
  mapImg: null,
  newMapImg: null,
  mapKey: null,
  overlayFC: null,
  placeFC: null,
  gatewayFC: null,
  assetFC: null,
  stationaryFC: null,
  scale: null,
  placeList: null,
  assetList: null,
  gatewayList: null,
  stationaryList: null,
  isHydrating: false,

  // imageRefetch and overlayRefetch default to no-op functions
  imageRefetch: () => {},
  overlayRefetch: () => {},
};

export const createMapDataSlice: StateCreator<MapStore, [], [], MapDataSlice> = (
  set,
  get,
  api,
) => ({
  ...mapDataInitialState,

  setMapImg: (val) => set({ mapImg: val }),
  setNewMapImg: (val) => set({ newMapImg: val }),
  setMapKey: (val) => set({ mapKey: val }),
  setOverlayFC: (val) => set({ overlayFC: val }),
  setPlaceFC: (val) => set({ placeFC: val }),
  setGatewayFC: (val) => set({ gatewayFC: val }),
  setAssetFC: (val) => set({ assetFC: val }),
  setStationaryFC: (val) => set({ stationaryFC: val }),
  setScale: (val) => set({ scale: val }),
  setPlaceList: (val) => set({ placeList: val }),
  setAssetList: (val) => set({ assetList: val }),
  setGatewayList: (val) => set({ gatewayList: val }),
  setStationaryList: (val) => set({ stationaryList: val }),
  setIsHydrating: (val) => set({ isHydrating: val }),

  fetchMapImage: async () => {
    const { mapID } = get();
    const response = await Api.get(`v2/maps/${mapID}/image`);
    return response.config.url;
  },
  fetchMapOveraly: async () => {
    const { mapID } = get();
    const { data } = await Api.get(`v2/maps/${mapID}`);
    return data;
  },
  fetchMapGateways: async (payload: any) => {
    const { mapID } = get();
    const { data } = await Api.post(`v2/maps/${mapID}/gateways/filter`, payload);
    return data;
  },
  fetchMapPlaces: async (payload: any) => {
    const { mapID } = get();
    const { data } = await Api.post(`v2/maps/${mapID}/places/filter`, payload);
    return data;
  },
  fetchMapAssets: async (payload: any) => {
    const { mapID } = get();
    const { data } = await Api.post(`v2/maps/${mapID}/assets/filter`, payload);
    return data;
  },
  fetchMapStationary: async (payload: any) => {
    const { mapID } = get();
    const { data } = await Api.post(`v2/maps/${mapID}/stationary-assets/filter`, payload);
    return data;
  },

  imageRefetch: () => {}, // no-op default
  setImageRefetch: (fn) => set({ imageRefetch: fn }),

  overlayRefetch: () => {}, // no-op default
  setOverlayRefetch: (fn) => set({ overlayRefetch: fn }),

  resetAsset: () => {
    const { setAssetList, setAssetFC } = get();
    setAssetList(null);
    setAssetFC(null);
  },
  resetPlace: () => {
    const { setPlaceList, setPlaceFC } = get();
    setPlaceList(null);
    setPlaceFC(null);
  },
  resetGateway: () => {
    const { setGatewayList, setGatewayFC } = get();
    setGatewayList(null);
    setGatewayFC(null);
  },
  resetStationary: () => {
    const { setStationaryList, setStationaryFC } = get();
    setStationaryList(null);
    setStationaryFC(null);
  },

  submitUpgrade: async () => {
    const {
      setIsLoading,
      setUpgradeInProg,
      setIsStatic,
      mapID,
      overlayFC,
      resetPlace,
      resetGateway,
      resetAsset,
      resetStationary,
      overlayRefetch,
      imageRefetch,
    } = get();

    setIsLoading(true);
    try {
      await Api.post(`/v2/maps/${mapID}/convert`, overlayFC);

      resetPlace();
      resetGateway();
      resetAsset();
      resetStationary();

      setUpgradeInProg(false);
      setIsStatic(false);

      if (overlayRefetch) overlayRefetch();
      if (imageRefetch) imageRefetch();

      toast.success('Successfully converted the current map to geospatial!');
      return true;
    } catch (error) {
      console.error(error);
      toast.error('Failed to convert the current map to geospatial.');
      return false;
    } finally {
      setIsLoading(false);
    }
  },

  saveMap: async () => {
    const {
      mapID,
      overlayFC,
      gatewayFC,
      placeFC,
      stationaryFC,
      scale,
      setIsLoading,
      imageRefetch,
      overlayRefetch,
      isHydrating,
    } = get();

    if (isHydrating) {
      toast.error('Please wait for the map data to finish loading.');
      return;
    }

    setIsLoading(true);
    try {
      if (overlayFC) await Api.put(`/v2/maps/${mapID}`, overlayFC);
      if (gatewayFC) await Api.put(`/v2/maps/${mapID}/gateways`, gatewayFC);
      if (placeFC) await Api.put(`/v2/maps/${mapID}/places`, placeFC);
      if (stationaryFC) await Api.put(`/v2/maps/${mapID}/stationary-assets`, stationaryFC);
      if (scale) {
        await Api.post(`/v2/maps/${mapID}/scale`, scale);
      }

      toast.success('Map was successfully updated!');

      set((state) => ({
        ...state,
        placeList: state.placeFC,
        gatewayList: state.gatewayFC,
        stationaryList: state.stationaryFC,
        clickEvent: null,
        showMore: null,
        drawEvent: null,
        newObjId: null,
        newObjName: null,
        newObjKeywords: null,
        newObjFloor: null,
        deleteItem: null,
        updateItem: false,
      }));
      if (imageRefetch) imageRefetch();
      if (overlayRefetch) overlayRefetch();
    } catch (error) {
      console.error(error);
      toast.error('Failed to update the current map.');
    } finally {
      setIsLoading(false);
    }
  },
});
